import { useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetWHOrdersQuery } from "store/api/warehouseManagerApi"
import { useAppDispatch } from "store/hooks"
import { setSelectedWHOrder } from "store/tempDataSlise"
import { warehouseManagerOrderStatusOptions } from "constants/SelectFieldOptions"
import { UITypes } from "types"

import { convertWHManagerOrdersToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { PackingDeviceModalOld } from "modals/PackingDeviceModalOld"
import { ShippingModalOld } from "modals/ShippingModalOld"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { StatusOfWHOrderOld } from "components/table/columnComponents/StatusOfWHOrderOld"
import TableWrapper from "components/table/TableWrapper"
import { WidgetOrdersOld } from "components/widgets/WidgetOrdersOld"
import { WidgetWarehouse } from "components/widgets/WidgetWarehouse"

import styles from "./index.module.scss"

// TABLE HEADER
const tableHeaderCells: UITypes.TableHeaderCell[] = [
  {
    title: "Order number",
    type: "sort-search",
    size: "middle",
    name: "order_number",
  },
  {
    title: "Status",
    type: "select",
    size: "middle",
    name: "status",
    options: warehouseManagerOrderStatusOptions,
    cellComponent: StatusOfWHOrderOld,
  },
  {
    title: "Company name",
    type: "sort-search",
    name: "company_name",
  },
  {
    title: "Brand name",
    type: "sort-search",
    name: "brand_name",
  },
  {
    title: "Order quantity",
    type: "sort-search",
    size: "middle",
    name: "order_quantity",
  },
  {
    title: "Contractor ID",
    type: "sort-search",
    size: "middle",
    name: "contractor_id",
  },
]

export const OrdersOld = () => {
  const [triggerGetWHOrders, { isFetching, data }] = useLazyGetWHOrdersQuery()
  const { urlQueryParams } = useUrlQueryParams()
  const dispatch = useAppDispatch()
  const [isFirstRender, setFirstRender] = useState(true)
  const [showPackingOrderModal, setShowPackingOrderModal] = useState(false)
  const [showShippingOrderModal, setShowShippingOrderModal] = useState(false)
  const [updateIndex, setUpdateIndex] = useState(0)

  const sendRequest = async (params: Record<string, string>) => {
    try {
      const res = await triggerGetWHOrders(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchData = () => {
    sendRequest(urlQueryParams)
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    fetchData()
  }, [urlQueryParams, isFirstRender])

  const onPackingClick = (id: number) => {
    const result = data?.data?.find(item => item.id === id)
    dispatch(setSelectedWHOrder(result))
    setShowPackingOrderModal(true)
  }

  const onShippingClick = (id: number) => {
    const result = data?.data?.find(item => item.id === id)
    dispatch(setSelectedWHOrder(result))
    setShowShippingOrderModal(true)
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => ({
    className: styles.tableActionsWrapper,
    actionButtons: [
      { icon: "Packing", hidden: rowData.rowSettings.status !== "confirmed", handler: onPackingClick },
      {
        icon: "Shipping",
        hidden: rowData.rowSettings.status !== "packed",
        handler: onShippingClick,
      },
    ],
  })

  const handleSuccess = () => {
    setShowPackingOrderModal(false)
    setShowShippingOrderModal(false)
    triggerGetWHOrders({})
    setUpdateIndex(prev => prev + 1)
    fetchData()
  }

  return (
    <>
      <div className={styles.widgetsWrapper}>
        <WidgetOrdersOld updateIndex={updateIndex} />
        <WidgetWarehouse updateIndex={updateIndex} />
      </div>
      <ContentWrapper title="Orders">
        <TableWrapper
          headerCells={tableHeaderCells}
          isLoading={isFetching}
          data={convertWHManagerOrdersToTableRows(data?.data)}
          getRowActions={getRowActions}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {showPackingOrderModal && (
        <PackingDeviceModalOld onCancel={() => setShowPackingOrderModal(false)} onSuccess={handleSuccess} />
      )}
      {showShippingOrderModal && (
        <ShippingModalOld onCancel={() => setShowShippingOrderModal(false)} onSuccess={handleSuccess} />
      )}
    </>
  )
}
